import React from 'react';
import PropTypes from 'prop-types';
import CommonAppSetup from '../../../common/common-app-setup/CommonAppSetup';
import ValidationType from '../../../common/validation/ValidationType';
import ValidationEngine from '../../../common/validation/ValidationEngine';
import InputContainer from '../input-container/InputContainer';
import keycode from 'keycode';
import Components from '../../../common/Components';
import joinClassNames from "../../../common/JoinClassNames";
import DirtyEngine from "../../../common/dirty/DirtyEngine";
import Size from "../../input-list/Size";

const propTypes = {
	// what extra classes should go on the input-container wrapper around this input?
	className: PropTypes.string,

	// tracks dirty state of fields
	dirtyEngine: PropTypes.instanceOf(DirtyEngine),

	// if true then the input will be disabled
	disabled: PropTypes.bool,

	// An error message to show. Overrides validation errors.
	errorMessage: PropTypes.string,

	// the name of the redux field being edited and id of the input for label. for=
	field: PropTypes.string.isRequired,

	// is this input "dirty" - trumps DirtyEngine
	isDirty: PropTypes.bool,

	// the label/placeholder to show
	label: PropTypes.string.isRequired,

	// should the file input allow multiple file selection
	multiple: PropTypes.bool,

	// triggers when the field loses focus
	onBlur: PropTypes.func,

	// what function to trigger when a change is detected (could be a dispatcher);
	// parameters: field, value
	onChange: PropTypes.func,

	// can optionally pass a callback for when the escape key is pressed
	onEscapePress: PropTypes.func,

	// read only input
	readOnly: PropTypes.bool,

	// When the component mounts should the input be given focus.
	setInitialFocus: PropTypes.bool,

	// should the label be shown?
	showLabel: PropTypes.bool,

	// css size style names: use Size.js constants
	size: PropTypes.string,

	// can skin (replace) the input with your own input; the file input still exists, but is hidden so that clicking your component will then click the file input
	skinComponent: PropTypes.object,

	// the validation rules and results
	validationEngine: PropTypes.instanceOf(ValidationEngine),

	// the value of this field: File Inputs are "Uncontrolled" so the value is only for Dirty Engine's sake
	value: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
		PropTypes.object,
		PropTypes.array,
	]),
};

const defaultProps = {
	className: undefined,
	dirtyEngine: undefined,
	disabled: false,
	errorMessage: undefined,
	isDirty: undefined,
	multiple: false,
	onBlur: undefined,
	onChange: undefined,
	onEscapePress: undefined,
	readOnly: false,
	setInitialFocus: false,
	showLabel: true,
	size: Size.MEDIUM,
	skinComponent: undefined,
	validationEngine: new ValidationEngine(),
	value: '',
};

export default class FileInput extends React.Component {
	constructor(props) {
		super(props);

		this.fileInput = React.createRef();
		this.skinWrapper = React.createRef();

		this.state = {
			hasBlurred: false
		};

		this.dirtyEngine = this.props.dirtyEngine ? this.props.dirtyEngine : new DirtyEngine({[this.props.field]: this.props.value});
	}

	componentDidMount() {
		if (this.props.setInitialFocus) {
			if (this.props.skinComponent) {
				this.skinWrapper.current.focus();
			} else {
				this.fileInput.current.focus();
			}
		}
	}

	onKeyDown = e => {
		if (this.props.onChange && keycode.isEventKey(e, keycode.codes.esc) && (!this.props.onEscapePress || !this.props.onEscapePress(e))) {
			e.preventDefault();
			e.stopPropagation();
		}

	};

	fileSelected = () => {
		// length can be 0 if the user cancelled selecting a file
		if (this.props.onChange) {
			this.props.onChange(this.fileInput.current.files);
		}
	};

	onBlur = () => {
		this.setState({hasBlurred: true});
		if (this.props.onBlur) {
			this.props.onBlur();
		}
	};

	render() {
		const validationError = this.props.validationEngine.getFormError(this.props.field, this.state.hasBlurred, this.props.errorMessage);
		const required = this.props.validationEngine.fieldHasRule(this.props.field, ValidationType.REQUIRED);
		return (
			<InputContainer className={joinClassNames(
				this.props.className,
				required ? 'required' : '',
				validationError ? 'error' : undefined,
				this.props.size,
				'file-input',
				this.props.skinComponent ? 'file-input--skin' : undefined,
				CommonAppSetup.getClassName(Components.FILE_INPUT, 'wrapper'),
				this.dirtyEngine.isDirtyClassName({[this.props.field]: this.props.value}, this.props.field, this.props.isDirty)
			)}>
				<label htmlFor={this.props.field} className={joinClassNames(
					this.props.showLabel ? '' : 'visually-hidden',
					CommonAppSetup.getClassName(Components.FILE_INPUT, 'label')
				)}>{this.props.label}</label>
				<input
					className={joinClassNames(
						this.props.showLabel ? '' : 'no-label',
						this.props.size,
						this.props.skinComponent ? 'visually-hidden' : undefined,
						CommonAppSetup.getClassName(Components.FILE_INPUT, 'input'),
					)}
					disabled={this.props.disabled}
					id={this.props.field}
					name={this.props.field}
					onBlur={this.onBlur}
					onChange={this.fileSelected}
					onKeyDown={this.onKeyDown}
					readOnly={this.props.readOnly}
					ref={this.fileInput}
					required={required ? 'required' : undefined}
					type="file"
					multiple={this.props.multiple}
				/>
				{this.props.skinComponent &&
					<div
						className={joinClassNames(
							'file-input__skin-wrapper',
							this.props.showLabel ? '' : 'no-label',
							this.props.size,
							CommonAppSetup.getClassName(Components.FILE_INPUT, 'input'),
						)}
						aria-disabled={this.props.disabled}
						onBlur={this.onBlur}
						onClick={() => this.fileInput.current.click()}
						onKeyDown={e => (keycode.isEventKey(e, keycode.codes.enter) || keycode.isEventKey(e, keycode.codes.space)) && this.fileInput.current.click()}
						ref={this.skinWrapper}
						role="button"
						tabIndex={0}
					>
						{this.props.skinComponent}
					</div>
				}
				{validationError}
			</InputContainer>
		);
	}
}

FileInput.propTypes = propTypes;
FileInput.defaultProps = defaultProps;